// File: apps/web/src/features/import/UploadZone.tsx
// Version: 2026.4.7 | Sprint 7 Wave 3 — Drag-drop upload zone
//
// Receives a file via drag-drop or file picker, validates basic shape,
// and hands it to the parent. The parent decides whether to call the
// preview endpoint or commit straight to import.

import { useCallback, useRef, useState, type DragEvent } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';

interface UploadZoneProps {
  /** Called when the user picks a valid file. */
  onFileSelected: (file: File) => void;
  /** When true, the zone is disabled and shows a busy overlay. */
  busy?:          boolean;
  /** Optional helper text shown under the icon. */
  hint?:          string;
}

const ACCEPT_EXTENSIONS = ['.zip'];
const MAX_BYTES         = 500 * 1024 * 1024; // matches API MaxUploadSizeBytes

export function UploadZone({ onFileSelected, busy = false, hint }: UploadZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  const validate = (file: File): string | null => {
    if (!ACCEPT_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))) {
      return `File must be one of: ${ACCEPT_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_BYTES) {
      return `File is too large (${(file.size / 1024 / 1024).toFixed(0)} MB > 500 MB)`;
    }
    if (file.size === 0) {
      return 'File is empty';
    }
    return null;
  };

  const handleFile = useCallback((file: File | undefined | null) => {
    if (!file) return;
    const err = validate(file);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onFileSelected(file);
  }, [onFileSelected]);

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!busy) setDragging(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const onPickClick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onPickClick}
      role="button"
      tabIndex={busy ? -1 : 0}
      style={{
        position:        'relative',
        padding:         '48px 32px',
        borderRadius:    16,
        border:          `2px dashed ${dragging ? C.neon : C.faint}`,
        background:      dragging
          ? `radial-gradient(ellipse at center, ${C.neon}14 0%, transparent 70%)`
          : C.surface,
        cursor:          busy ? 'not-allowed' : 'pointer',
        textAlign:       'center',
        transition:      'all 0.2s ease',
        opacity:         busy ? 0.5 : 1,
        fontFamily:      F.display,
        userSelect:      'none',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_EXTENSIONS.join(',')}
        onChange={e => handleFile(e.target.files?.[0])}
        style={{ display: 'none' }}
      />

      <div style={{
        fontSize:       64,
        marginBottom:   16,
        filter:         dragging ? `drop-shadow(0 0 16px ${C.neonGlow})` : 'none',
        transition:     'filter 0.2s ease',
      }}>
        📥
      </div>

      <div style={{
        fontSize:    20,
        fontWeight:  600,
        color:       C.text,
        marginBottom: 8,
      }}>
        {dragging ? 'Drop your export here' : 'Drop your claude.ai export'}
      </div>

      <div style={{
        fontSize:    14,
        color:       C.muted,
        marginBottom: 16,
      }}>
        or <span style={{ color: C.neon, textDecoration: 'underline' }}>browse files</span>
        {' '}— up to 500 MB
      </div>

      {hint && (
        <div style={{
          fontSize:   12,
          color:      C.dim,
          fontFamily: F.mono,
        }}>
          {hint}
        </div>
      )}

      {error && (
        <div style={{
          marginTop:    16,
          padding:      '8px 16px',
          borderRadius: 8,
          background:   `${C.coral}14`,
          border:       `1px solid ${C.coral}38`,
          color:        C.coral,
          fontSize:     13,
          display:      'inline-block',
        }}>
          {error}
        </div>
      )}
    </div>
  );
}
